import { Component, createSignal, Show } from 'solid-js';
import { usePreferences } from '../providers/PreferencesProvider';

interface AlgorithmControlsProps {
  onUpdate?: () => void; // Callback when values change
}

export const AlgorithmControls: Component<AlgorithmControlsProps> = (props) => {
  const { preferences, updatePreference } = usePreferences();
  const [isOpen, setIsOpen] = createSignal(false);

  const handleSliderUpdate = (key: string, value: number) => {
    updatePreference(key as any, value);
    props.onUpdate?.();
  };

  const handleToggleUpdate = (key: string, value: boolean) => {
    updatePreference(key as any, value);
    props.onUpdate?.();
  };

  return (
    <div class="mb-4">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen())}
        class="flex items-center gap-2 px-3 py-2 text-sm border border-[var(--border-color)] rounded hover:border-[var(--accent)] transition-colors"
        classList={{
          'bg-[var(--accent)] text-white border-[var(--accent)]': isOpen(),
        }}
      >
        <span>&#9881;</span>
        <span>Algorithm</span>
        <span class="text-xs opacity-70">{isOpen() ? '\u25BC' : '\u25B6'}</span>
      </button>

      {/* Compact Slider Box */}
      <Show when={isOpen()}>
        <div class="mt-3 p-4 border border-[var(--border-color)] rounded bg-[var(--bg-secondary)] space-y-3">
          <div class="text-xs font-semibold text-text-secondary mb-3 opacity-70">
            Feed Ranking Weights
          </div>

          {/* Reply Weight */}
          <div class="space-y-1">
            <label class="flex justify-between text-xs text-text-secondary">
              <span>Reply Weight</span>
              <span class="font-mono">{preferences().replyWeight.toFixed(1)}</span>
            </label>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={preferences().replyWeight}
              onInput={(e) => handleSliderUpdate('replyWeight', Number(e.currentTarget.value))}
              class="w-full h-1"
            />
          </div>

          {/* Repost Weight */}
          <div class="space-y-1">
            <label class="flex justify-between text-xs text-text-secondary">
              <span>Repost Weight</span>
              <span class="font-mono">{preferences().repostWeight.toFixed(1)}</span>
            </label>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={preferences().repostWeight}
              onInput={(e) => handleSliderUpdate('repostWeight', Number(e.currentTarget.value))}
              class="w-full h-1"
            />
          </div>

          {/* Reply Count Weight */}
          <div class="space-y-1">
            <label class="flex justify-between text-xs text-text-secondary">
              <span>Reply Count Weight</span>
              <span class="font-mono">{preferences().replyCountWeight.toFixed(1)}</span>
            </label>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={preferences().replyCountWeight}
              onInput={(e) => handleSliderUpdate('replyCountWeight', Number(e.currentTarget.value))}
              class="w-full h-1"
            />
          </div>

          {/* Decay Lambda */}
          <div class="space-y-1">
            <label class="flex justify-between text-xs text-text-secondary">
              <span>Time Decay</span>
              <span class="font-mono">{preferences().decayLambda.toFixed(3)}</span>
            </label>
            <input
              type="range"
              min="0.001"
              max="1"
              step="0.001"
              value={preferences().decayLambda}
              onInput={(e) => handleSliderUpdate('decayLambda', Number(e.currentTarget.value))}
              class="w-full h-1"
            />
          </div>

          {/* Subtract Negative PoW Toggle */}
          <div class="flex items-center justify-between">
            <span class="text-xs text-text-secondary">Subtract Negative PoW</span>
            <button
              onClick={() => handleToggleUpdate('subtractNegative', !preferences().subtractNegative)}
              class="text-xs px-2 py-1 rounded border border-[var(--border-color)]"
              classList={{
                'bg-[var(--accent)] text-white': preferences().subtractNegative,
              }}
            >
              {preferences().subtractNegative ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* Subtract Reports Toggle */}
          <div class="flex items-center justify-between">
            <span class="text-xs text-text-secondary">Subtract Reports</span>
            <button
              onClick={() => handleToggleUpdate('subtractReports', !preferences().subtractReports)}
              class="text-xs px-2 py-1 rounded border border-[var(--border-color)]"
              classList={{
                'bg-[var(--accent)] text-white': preferences().subtractReports,
              }}
            >
              {preferences().subtractReports ? 'ON' : 'OFF'}
            </button>
          </div>

          <div class="pt-2 border-t border-[var(--border-color)]">
            <a
              href="/preferences"
              class="text-xs text-[var(--accent)] hover:underline"
            >
              More settings &rarr;
            </a>
          </div>
        </div>
      </Show>
    </div>
  );
};
